import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const RESTORE_WINDOW_MS = 2000;

/**
 * Restores `window` scroll position on back/forward navigation and on REPLACE
 * navigations back to a previously visited path (e.g. route-tab switches, so
 * each tab keeps its own scroll). Positions are keyed by pathname+search — a
 * PUSH to a brand-new page still starts at the top. Native scroll restoration
 * handles the POP case for fully-rendered documents, but our pages fetch data
 * asynchronously and lazy-load via Suspense — so right after `popstate` the
 * destination page is short, the browser clamps the saved offset, and the
 * page settles at the wrong scroll position once async content fills in. This
 * component watches the body for size changes after a restore and retries as
 * content grows, up to a short window.
 */
export const JcScrollRestoration = (): null => {
    const location = useLocation();
    const navigationType = useNavigationType();
    const positions = useRef<Map<string, number>>(new Map());
    const pathKey = location.pathname + location.search;

    useLayoutEffect(() => {
        if ("scrollRestoration" in window.history) {
            window.history.scrollRestoration = "manual";
        }
    }, []);

    useLayoutEffect(() => {
        return () => { positions.current.set(pathKey, window.scrollY); };
    }, [pathKey]);

    useLayoutEffect(() => {
        const target = navigationType === "POP" || navigationType === "REPLACE"
            ? positions.current.get(pathKey) ?? 0
            : 0;

        if (target === 0) {
            window.scrollTo(0, 0);
            return;
        }

        let done = false;
        const finish = (): void => {
            if (done) return;
            done = true;
            observer.disconnect();
            window.clearTimeout(timer);
            window.removeEventListener("wheel", finish);
            window.removeEventListener("touchmove", finish);
            window.removeEventListener("keydown", finish);
        };

        const tryRestore = (): void => {
            if (done) return;
            const max = document.documentElement.scrollHeight - window.innerHeight;
            window.scrollTo(0, Math.min(target, Math.max(max, 0)));
            if (max >= target) finish();
        };

        const observer = new ResizeObserver(tryRestore);
        observer.observe(document.body);
        const timer = window.setTimeout(finish, RESTORE_WINDOW_MS);
        window.addEventListener("wheel", finish, { passive: true });
        window.addEventListener("touchmove", finish, { passive: true });
        window.addEventListener("keydown", finish);

        tryRestore();

        return finish;
    }, [pathKey, navigationType]);

    return null;
};
